import React from "react";
import type { User } from "../../../../api/client/types/user.types";
import type { PaginatedResponse } from "../../../../api/client/types/common.types";
import { PaginatedTable } from "../../../../shared/components/ui/PaginatedTable";
import { StatusBadge } from "../../../../shared/components/ui/StatusBadge";
import { EditButton } from "../../../../shared/components/ui/EditButton";
import { type ColumnDef } from "@tanstack/react-table";
import { TRANSLATIONS } from "../../../../shared/utils/translations";
import { pluralize } from "../../../../shared/utils/stringUtils";

interface UserTableProps {
  users: PaginatedResponse<User>;
  isLoading?: boolean;
  isError?: boolean;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;

  /** Callback when the user clicks the edit icon */
  onEditUser?: (user: User) => void;

  /** Optional count of active users to display instead of total */
  activeUsersCount?: number;
}

export const UserTable: React.FC<UserTableProps> = ({
  users,
  isLoading,
  isError,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onEditUser,
  activeUsersCount,
}) => {
  const columns = React.useMemo<ColumnDef<User>[]>(
    () => [
      {
        id: "actions",
        header: "Acciones",
        cell: ({ row }) => (
          <div className="flex items-center gap-1">
            <EditButton
              onClick={() => onEditUser?.(row.original)}
              title="Editar usuario"
              iconSize={16}
            />
            <button
              type="button"
              onClick={() => onEditUser?.(row.original)}
              className="text-xs leading-none text-gray-500 hover:text-blue-600 transition-colors cursor-pointer"
              title="Editar usuario"
            ></button>
          </div>
        ),
        enableSorting: false,
      },
      {
        header: "Nombre",
        accessorKey: "name",
        cell: ({ getValue }) => (
          <div className="line-clamp-4" title={getValue() as string}>
            {getValue() as string}
          </div>
        ),
      },
      { header: "Cédula", accessorKey: "citizenship_number" },
      {
        header: "Estado",
        accessorKey: "status.name",
        cell: ({ row }) => {
          const statusCode = row.original.status.name;
          const translatedStatus =
            TRANSLATIONS.status[
            statusCode as keyof typeof TRANSLATIONS.status
            ] || statusCode;
          return (
            <StatusBadge statusCode={statusCode} label={translatedStatus} />
          );
        },
      },
      {
        header: "Creado el",
        accessorKey: "created_at",
        cell: ({ getValue }) =>
          new Date(getValue() as string).toLocaleDateString("es-CO"),
      },
    ],
    [onEditUser]
  );

  return (
    <PaginatedTable<User>
      data={users}
      columns={columns}
      isLoading={isLoading}
      isError={isError}
      page={page}
      pageSize={pageSize}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      totalLabel="Total"
      entityName={activeUsersCount ? 
        pluralize(activeUsersCount, "usuario activo", "usuarios activos") 
        : pluralize(users?.total ?? 0, "usuario", "usuarios")}
      customTotal={activeUsersCount}
      tableContainerClassName="overflow-x-auto border rounded-lg"
    />
  );
};
